import { useState, useEffect } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { hasPermission, NAV_TOP, NAV_SETTINGS, MODULE_TABS, ShellContext } from '../lib/permissions'
import styles from './AppShell.module.css'
import CommandPalette from './CommandPalette'
import NotificationsPanel from './NotificationsPanel'
import TimerWidget from '../modules/projects/TimerWidget'

/* ── Nav icons (16×16, colored per module like the mockup) ── */
function IconSearch() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="var(--ac)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" /><line x1="10.5" y1="10.5" x2="13.5" y2="13.5" />
    </svg>
  )
}
function IconFunnel() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="var(--ye)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 3h11l-4 5.5v5l-3-1.5V8.5z" />
    </svg>
  )
}
function IconBars() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="var(--gr)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="13" x2="13" y2="13" />
      <rect x="4" y="8" width="2" height="5" /><rect x="7" y="5" width="2" height="8" /><rect x="10" y="3" width="2" height="10" />
    </svg>
  )
}
function IconGear() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="8" r="2.5" />
      <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
    </svg>
  )
}
function IconChevronLeft() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="10,4 6,8 10,12" />
    </svg>
  )
}

function IconGrid() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="var(--ac)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="9" y="2.5" width="4.5" height="4.5" rx="1" />
      <rect x="2.5" y="9" width="4.5" height="4.5" rx="1" /><rect x="9" y="9" width="4.5" height="4.5" rx="1" />
    </svg>
  )
}

function IconBoard() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="var(--gr)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2.5" width="3.5" height="11" rx="1" /><rect x="6.5" y="2.5" width="3.5" height="7" rx="1" />
      <rect x="11" y="2.5" width="3.5" height="9" rx="1" />
    </svg>
  )
}

function IconLifebuoy() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="var(--re)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="8" r="6" /><circle cx="8" cy="8" r="2.4" />
      <path d="M3.8 3.8l2.4 2.4M9.8 9.8l2.4 2.4M12.2 3.8L9.8 6.2M6.2 9.8l-2.4 2.4" />
    </svg>
  )
}

const MODULE_ICONS: Record<string, JSX.Element> = {
  'dashboard':    <IconGrid />,
  'lead-engine':  <IconSearch />,
  'sales-engine': <IconFunnel />,
  'projects':     <IconBoard />,
  'service-desk': <IconLifebuoy />,
  'intelligence': <IconBars />,
  'settings':     <IconGear />,
}

function getActiveModule(pathname: string): string {
  if (pathname === '/app' || pathname === '/app/') return 'dashboard'
  if (pathname.startsWith('/app/leads'))   return 'lead-engine'
  if (pathname.startsWith('/app/deals'))    return 'sales-engine'
  if (pathname.startsWith('/app/projects')) return 'projects'
  if (pathname.startsWith('/app/tickets'))  return 'service-desk'
  if (pathname.startsWith('/app/reports'))  return 'intelligence'
  if (pathname.startsWith('/app/admin'))   return 'settings'
  return ''
}

interface SidebarItemProps {
  id: string; label: string; path: string; isActive: boolean; collapsed: boolean
}
function SidebarItem({ id, label, path, isActive, collapsed }: SidebarItemProps) {
  return (
    <div className={styles.niWrap}>
      <NavLink to={path} className={`${styles.ni} ${isActive ? styles.niOn : ''}`}>
        <span className={styles.niIc}>{MODULE_ICONS[id]}</span>
        {!collapsed && <span className={styles.niLbl}>{label}</span>}
      </NavLink>
      {collapsed && <span className={styles.niTip}>{label}</span>}
    </div>
  )
}

export default function AppShell() {
  const { claims, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true')
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [brandLogo, setBrandLogo] = useState<string | null>(null)
  const [moreOpen, setMoreOpen] = useState(false)

  // Apply org branding (accent + logo) once authenticated
  useEffect(() => {
    api.get<{ accent_color: string | null; logo_data_url: string | null }>('/api/auth/branding')
      .then(b => {
        if (b.accent_color) document.documentElement.style.setProperty('--ac', b.accent_color)
        if (b.logo_data_url) setBrandLogo(b.logo_data_url)
      }).catch(() => {})
  }, [])

  const activeModule = getActiveModule(location.pathname)
  const tabs = MODULE_TABS[activeModule] ?? []

  useEffect(() => { setActiveTab(0); setMoreOpen(false) }, [activeModule])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setPaletteOpen(o => !o)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  function toggleCollapsed() {
    setCollapsed(c => {
      const next = !c
      localStorage.setItem('sidebarCollapsed', String(next))
      return next
    })
  }

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  const shellCtx: ShellContext = { activeTab, setActiveTab, tabs }
  const initials = claims ? claims.email.slice(0, 2).toUpperCase() : '?'

  return (
    <div className={`${styles.shell} ${collapsed ? styles.collapsed : ''}`}>

      {/* ── Sidebar (full height) ── */}
      <nav className={styles.sidebar} aria-label="Main navigation">
        <div className={styles.brand}>
          {collapsed
            ? <span className={styles.brandMark}>CH</span>
            : <img src={brandLogo || '/brand/cyberhelix-logo.png'} alt="Cyberhelix" className={styles.brandLogo} />}
        </div>

        <div className={styles.navBody}>
          {NAV_TOP.map(mod => {
            if (!claims || !hasPermission(claims, mod.permissionKey)) return null
            return (
              <SidebarItem
                key={mod.id} id={mod.id} label={mod.label} path={mod.path}
                isActive={activeModule === mod.id} collapsed={collapsed}
              />
            )
          })}
        </div>

        {/* Settings — pinned above user bar */}
        <div className={styles.navSettings}>
          {claims && hasPermission(claims, NAV_SETTINGS.permissionKey) && (
            <SidebarItem
              id={NAV_SETTINGS.id} label={NAV_SETTINGS.label} path={NAV_SETTINGS.path}
              isActive={activeModule === 'settings'} collapsed={collapsed}
            />
          )}
        </div>

        <div className={styles.sbBottom}>
          {claims && (
            <button className={styles.userBar} onClick={handleLogout} title="Sign out">
              <span className={styles.uAva}>{initials}</span>
              {!collapsed && (
                <span className={styles.userInfo}>
                  <span className={styles.uName}>{claims.email}</span>
                  <span className={styles.uRole}>{claims.role.replace('_', ' ')}</span>
                </span>
              )}
            </button>
          )}
          <button
            className={styles.collapseBtn}
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <IconChevronLeft />
          </button>
        </div>
      </nav>

      {/* ── Main area ── */}
      <div className={styles.mainArea}>
        <header className={styles.topbar}>
          <button
            className={styles.cmdBtn}
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette (Ctrl+K)"
            aria-keyshortcuts="Control+K Meta+K"
          >
            <span className={styles.cmdPlaceholder}>⌕ Search or type a command…</span>
            <kbd className={styles.cmdHint}>⌘K</kbd>
          </button>
          <div className={styles.topbarRight}>
            <TimerWidget />
            <NotificationsPanel />
            {claims && (
              <button
                className={styles.topbarAvatar}
                onClick={handleLogout}
                title={`Sign out — ${claims.email}`}
                aria-label="Sign out"
              >
                {claims.email[0].toUpperCase()}
              </button>
            )}
          </div>
        </header>

        {tabs.length > 0 && (
          <div className={styles.subnav} role="tablist" aria-label="Module navigation">
            {tabs.map((tab, i) => (
              <button
                key={tab} role="tab" aria-selected={activeTab === i}
                className={`${styles.snt} ${activeTab === i ? styles.sntOn : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {tab}
              </button>
            ))}
          </div>
        )}

        <main className={styles.main} id="main-content">
          <Outlet context={shellCtx} />
        </main>
      </div>

      {/* ── Bottom navigation (mobile only — sidebar collapses to this) ──
           Keep it to 5 slots: the first 4 permitted modules inline + a "More"
           overflow sheet holding the rest (and Settings). Desktop uses the sidebar. */}
      {(() => {
        const permitted = NAV_TOP.filter(m => claims && hasPermission(claims, m.permissionKey))
        const primary = permitted.slice(0, 4)
        const overflow = permitted.slice(4)
        const settingsOk = !!(claims && hasPermission(claims, NAV_SETTINGS.permissionKey))
        const overflowItems = [...overflow, ...(settingsOk ? [NAV_SETTINGS] : [])]
        const moreActive = overflowItems.some(m => activeModule === m.id)
        return (
          <nav className={styles.bottomNav} aria-label="Primary navigation">
            {primary.map(mod => {
              const on = activeModule === mod.id
              return (
                <NavLink
                  key={mod.id} to={mod.path}
                  className={`${styles.bnItem} ${on ? styles.bnItemActive : ''}`}
                  aria-current={on ? 'page' : undefined}
                >
                  <span className={styles.bnIcon}>{MODULE_ICONS[mod.id]}</span>
                  <span className={styles.bnLabel}>{mod.label}</span>
                </NavLink>
              )
            })}
            {overflowItems.length > 0 && (
              <button
                type="button"
                className={`${styles.bnItem} ${moreActive ? styles.bnItemActive : ''}`}
                aria-haspopup="true" aria-expanded={moreOpen}
                onClick={() => setMoreOpen(o => !o)}
              >
                <span className={styles.bnIcon}>⋯</span>
                <span className={styles.bnLabel}>More</span>
              </button>
            )}
          </nav>
        )
      })()}

      {/* ── Mobile "More" overflow sheet ── */}
      {moreOpen && (
        <div className={styles.moreScrim} onClick={() => setMoreOpen(false)}>
          <div className={styles.moreSheet} role="menu" aria-label="More navigation" onClick={e => e.stopPropagation()}>
            <div className={styles.moreHandle} />
            {[...NAV_TOP.filter(m => claims && hasPermission(claims, m.permissionKey)).slice(4),
              ...(claims && hasPermission(claims, NAV_SETTINGS.permissionKey) ? [NAV_SETTINGS] : [])
            ].map(mod => {
              const on = activeModule === mod.id
              return (
                <NavLink
                  key={mod.id} to={mod.path} role="menuitem"
                  className={`${styles.moreItem} ${on ? styles.moreItemActive : ''}`}
                  onClick={() => setMoreOpen(false)}
                  aria-current={on ? 'page' : undefined}
                >
                  <span className={styles.moreIcon}>{MODULE_ICONS[mod.id]}</span>
                  <span>{mod.label}</span>
                </NavLink>
              )
            })}
          </div>
        </div>
      )}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  )
}
